import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  adminListBkls,
  adminSaveBeat,
  adminSetBeatStatus,
  adminSaveTier,
  adminSetBeatTierPrice,
  adminSaveBeatFile,
  adminListBeatFiles,
  adminDeleteBeatFile,
  adminSaveLicenseTemplate,
  adminUpdateBklsSettings,
  adminUpdateCustomRequest,
  adminRetryRequestNotification,
} from "@/lib/bkls-admin.functions";
import { formatMoney, LEGAL_REVIEW_NOTICE } from "@/lib/bkls";

export const Route = createFileRoute("/_authenticated/beats/admin")({
  head: () => ({
    meta: [{ title: "Beats Admin · Buddha KA" }, { name: "robots", content: "noindex" }],
  }),
  component: BeatsAdmin,
});

const CHECKLIST = [
  "Entertainment-attorney review of every licence template recorded below",
  "Domain and SSL verified on buddhaka.com",
  "Payment provider confirmed in test mode, then explicitly approved for live",
  "Auth redirects verified for sign-in during checkout",
  "Transactional email delivery verified (licence receipt + custom request reply)",
  "Storage privacy verified: no public bucket exposure of masters or stems",
  "Signed download URLs verified as short-lived and logged",
  "Row-level security verified on beats, licences, downloads and requests",
  "Refund and revocation policy published",
  "Mobile checkout tested end to end",
  "Download testing on desktop and mobile",
  "Analytics events confirmed for catalog, licence view and purchase",
  "Backups and data export verified",
];

const input =
  "w-full rounded-lg border hairline bg-obsidian px-3 py-2 text-sm text-ivory placeholder:text-muted-foreground focus:border-gold focus:outline-none";
const label = "block text-[10px] uppercase tracking-[0.2em] text-gold/80";
const card = "rounded-2xl border hairline bg-charcoal p-6";
const btn =
  "rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous transition-all hover:bg-gold hover:text-obsidian disabled:opacity-50";
const btnGhost =
  "rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold";

function BeatsAdmin() {
  const load = useServerFn(adminListBkls);
  const saveBeat = useServerFn(adminSaveBeat);
  const setStatus = useServerFn(adminSetBeatStatus);
  const saveTier = useServerFn(adminSaveTier);
  const setPrice = useServerFn(adminSetBeatTierPrice);
  const saveFile = useServerFn(adminSaveBeatFile);
  const listFiles = useServerFn(adminListBeatFiles);
  const deleteFile = useServerFn(adminDeleteBeatFile);
  const saveTemplate = useServerFn(adminSaveLicenseTemplate);
  const saveSettings = useServerFn(adminUpdateBklsSettings);
  const updateRequest = useServerFn(adminUpdateCustomRequest);
  const retryNotification = useServerFn(adminRetryRequestNotification);

  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [files, setFiles] = useState<any[]>([]);

  const data = useQuery({
    queryKey: ["bkls", "admin"],
    queryFn: () => load({ data: {} } as never),
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["bkls", "admin"] });

  useEffect(() => {
    if (!selected?.id) {
      setFiles([]);
      return;
    }
    void listFiles({ data: { beat_id: selected.id } }).then((r) => setFiles(r.files));
  }, [selected?.id, listFiles]);

  if (data.isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 sm:px-6">
        <h1 className="font-serif text-3xl">Admin access required.</h1>
        <p className="mt-3 text-sm text-muted-foreground">This area is restricted to Buddha KA administrators.</p>
      </div>
    );
  }

  const d = data.data;
  const settings = d?.settings;

  async function onSaveBeat(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const bpmRaw = String(f.get("bpm") ?? "").trim();
    const result = await saveBeat({
      data: {
        ...(selected?.id ? { id: selected.id } : {}),
        slug: String(f.get("slug") ?? ""),
        title: String(f.get("title") ?? ""),
        subtitle: String(f.get("subtitle") ?? ""),
        description: String(f.get("description") ?? ""),
        status: String(f.get("status") ?? "draft") as "draft" | "published" | "archived",
        bpm: bpmRaw ? Number(bpmRaw) : null,
        musical_key: String(f.get("musical_key") ?? ""),
        genre: String(f.get("genre") ?? ""),
        mood: String(f.get("mood") ?? ""),
        tags: String(f.get("tags") ?? "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        artwork_path: String(f.get("artwork_path") ?? ""),
        preview_path: String(f.get("preview_path") ?? ""),
        sort_order: Number(f.get("sort_order") ?? 0),
      },
    }).catch((e) => ({ error: String(e) }));
    setMessage("error" in result ? result.error : "Beat saved.");
    if (!("error" in result)) {
      setSelected(null);
      void refresh();
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Licensing administration</p>
          <h1 className="mt-3 font-serif text-4xl">Beats.</h1>
        </div>
        <Link to="/beats" className="text-sm text-gold hover:text-gold-luminous">
          View public hub →
        </Link>
      </header>

      <p className="mt-8 rounded-2xl border border-gold/40 bg-charcoal p-5 text-sm text-ivory">
        <strong className="text-gold-luminous">Legal notice.</strong> {LEGAL_REVIEW_NOTICE}
      </p>

      {message && <p className="mt-6 text-sm text-gold">{message}</p>}

      {/* ---------- Settings / feature flag ---------- */}
      <section className={`${card} mt-10`} aria-labelledby="settings">
        <h2 id="settings" className="font-serif text-2xl text-gold-luminous">
          Launch controls
        </h2>
        <form
          className="mt-5 grid gap-4 sm:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const result = await saveSettings({
              data: {
                public_enabled: f.get("public_enabled") === "on",
                checkout_enabled: f.get("checkout_enabled") === "on",
                checkout_mode: (f.get("checkout_mode") as "sandbox" | "live") ?? "sandbox",
                legal_reviewed: f.get("legal_reviewed") === "on",
                legal_review_note: String(f.get("legal_review_note") ?? ""),
                support_email: String(f.get("support_email") ?? ""),
              },
            });
            setMessage("error" in result ? result.error : `Saved. Checkout mode: ${result.checkout_mode}.`);
            void refresh();
          }}
        >
          <label className="flex items-center gap-3 text-sm text-ivory">
            <input type="checkbox" name="public_enabled" defaultChecked={settings?.public_enabled} /> Show Beats in
            public navigation
          </label>
          <label className="flex items-center gap-3 text-sm text-ivory">
            <input type="checkbox" name="checkout_enabled" defaultChecked={settings?.checkout_enabled} /> Enable
            licence checkout
          </label>
          <div>
            <label className={label} htmlFor="checkout_mode">
              Checkout mode
            </label>
            <select id="checkout_mode" name="checkout_mode" defaultValue={settings?.checkout_mode ?? "sandbox"} className={input}>
              <option value="sandbox">Test (sandbox)</option>
              <option value="live">Live — requires recorded legal review</option>
            </select>
          </div>
          <div>
            <label className={label} htmlFor="support_email">
              Licensing contact email
            </label>
            <input id="support_email" name="support_email" defaultValue={settings?.support_email ?? ""} className={input} />
          </div>
          <label className="flex items-center gap-3 text-sm text-ivory sm:col-span-2">
            <input type="checkbox" name="legal_reviewed" defaultChecked={settings?.legal_reviewed} /> An
            entertainment attorney has reviewed the current licence templates
          </label>
          <div className="sm:col-span-2">
            <label className={label} htmlFor="legal_review_note">
              Review note (who reviewed, when)
            </label>
            <textarea id="legal_review_note" name="legal_review_note" rows={2} defaultValue={settings?.legal_review_note ?? ""} className={input} />
          </div>
          <button type="submit" className={`${btn} sm:col-span-2 justify-self-start`}>
            Save launch controls
          </button>
        </form>
      </section>

      {/* ---------- Beats ---------- */}
      <section className={`${card} mt-8`} aria-labelledby="beats">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="beats" className="font-serif text-2xl text-gold-luminous">
            Beats ({d?.beats.length ?? 0})
          </h2>
          <button type="button" className={btnGhost} onClick={() => setSelected({})}>
            New beat
          </button>
        </div>

        {(d?.beats.length ?? 0) === 0 && !selected && (
          <p className="mt-4 text-sm text-muted-foreground">
            The catalog is empty. Nothing is published until you create a beat and set it to published.
          </p>
        )}

        {(d?.beats ?? []).length > 0 && (
          <ul className="mt-5 space-y-2">
            {(d?.beats ?? []).map((b: any) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border hairline p-4">
                <div>
                  <p className="text-ivory">{b.title}</p>
                  <p className="text-xs text-muted-foreground">
                    /{b.slug} · {b.status} · {b.genre ?? "—"} · {b.bpm ?? "—"} BPM
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={btnGhost} onClick={() => setSelected(b)}>
                    Edit
                  </button>
                  {(["draft", "published", "archived"] as const)
                    .filter((s) => s !== b.status)
                    .map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={btnGhost}
                        onClick={async () => {
                          await setStatus({ data: { id: b.id, status: s } });
                          void refresh();
                        }}
                      >
                        {s}
                      </button>
                    ))}
                </div>
              </li>
            ))}
          </ul>
        )}

        {selected && (
          <form onSubmit={onSaveBeat} className="mt-6 grid gap-4 rounded-xl border hairline p-5 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="title">Title</label>
              <input id="title" name="title" required defaultValue={selected.title ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="slug">Slug (a–z, 0–9, hyphens)</label>
              <input id="slug" name="slug" required defaultValue={selected.slug ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="subtitle">Subtitle</label>
              <input id="subtitle" name="subtitle" defaultValue={selected.subtitle ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="status">Status</label>
              <select id="status" name="status" defaultValue={selected.status ?? "draft"} className={input}>
                <option value="draft">draft</option>
                <option value="published">published</option>
                <option value="archived">archived</option>
              </select>
            </div>
            <div>
              <label className={label} htmlFor="bpm">BPM</label>
              <input id="bpm" name="bpm" type="number" defaultValue={selected.bpm ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="musical_key">Key</label>
              <input id="musical_key" name="musical_key" defaultValue={selected.musical_key ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="genre">Genre</label>
              <input id="genre" name="genre" defaultValue={selected.genre ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="mood">Mood</label>
              <input id="mood" name="mood" defaultValue={selected.mood ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="artwork_path">Artwork path (media bucket)</label>
              <input id="artwork_path" name="artwork_path" defaultValue={selected.artwork_path ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="preview_path">Tagged preview path (media bucket)</label>
              <input id="preview_path" name="preview_path" defaultValue={selected.preview_path ?? ""} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="tags">Tags (comma separated)</label>
              <input id="tags" name="tags" defaultValue={(selected.tags ?? []).join(", ")} className={input} />
            </div>
            <div>
              <label className={label} htmlFor="sort_order">Sort order</label>
              <input id="sort_order" name="sort_order" type="number" defaultValue={selected.sort_order ?? 0} className={input} />
            </div>
            <div className="sm:col-span-2">
              <label className={label} htmlFor="description">Description</label>
              <textarea id="description" name="description" rows={4} defaultValue={selected.description ?? ""} className={input} />
            </div>
            <div className="flex gap-3 sm:col-span-2">
              <button type="submit" className={btn}>Save beat</button>
              <button type="button" className={btnGhost} onClick={() => setSelected(null)}>Cancel</button>
            </div>

            {selected.id && (
              <div className="sm:col-span-2 border-t hairline pt-5">
                <h3 className="font-serif text-lg text-ivory">Private files</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Masters and stems live in the private downloads bucket and are only ever released through
                  short-lived signed links tied to an issued licence.
                </p>
                <ul className="mt-3 space-y-2">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-muted-foreground">
                        {f.kind} · {f.label} · {f.storage_path}
                      </span>
                      <button
                        type="button"
                        className={btnGhost}
                        onClick={async () => {
                          await deleteFile({ data: { id: f.id } });
                          const r = await listFiles({ data: { beat_id: selected.id } });
                          setFiles(r.files);
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                  {files.length === 0 && <li className="text-sm text-muted-foreground">No files attached.</li>}
                </ul>
                <div className="mt-4 grid gap-3 sm:grid-cols-4">
                  <select id="file_kind" className={input} defaultValue="mp3">
                    <option value="mp3">mp3</option>
                    <option value="wav">wav</option>
                    <option value="stems">stems</option>
                    <option value="other">other</option>
                  </select>
                  <input id="file_label" placeholder="Label" className={input} />
                  <input id="file_path" placeholder="downloads/… storage path" className={`${input} sm:col-span-1`} />
                  <button
                    type="button"
                    className={btn}
                    onClick={async () => {
                      const kind = (document.getElementById("file_kind") as HTMLSelectElement).value as any;
                      const lbl = (document.getElementById("file_label") as HTMLInputElement).value;
                      const path = (document.getElementById("file_path") as HTMLInputElement).value;
                      if (!lbl || !path) return setMessage("File label and storage path are required.");
                      const r = await saveFile({ data: { beat_id: selected.id, kind, label: lbl, storage_path: path } });
                      setMessage("error" in r ? r.error : "File attached.");
                      const list = await listFiles({ data: { beat_id: selected.id } });
                      setFiles(list.files);
                    }}
                  >
                    Attach file
                  </button>
                </div>

                <h3 className="mt-6 font-serif text-lg text-ivory">Per-tier availability and price</h3>
                <div className="mt-3 space-y-2">
                  {(d?.tiers ?? []).map((t: any) => {
                    const override = (d?.prices ?? []).find(
                      (p: any) => p.beat_id === selected.id && p.tier_key === t.key,
                    );
                    return (
                      <div key={t.key} className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="w-32 text-ivory">{t.name}</span>
                        <input
                          id={`price-${t.key}`}
                          type="number"
                          placeholder={`${(t.price_cents / 100).toFixed(2)}`}
                          defaultValue={override?.price_cents != null ? override.price_cents / 100 : ""}
                          className={`${input} w-28`}
                        />
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                          <input type="checkbox" id={`avail-${t.key}`} defaultChecked={override?.available ?? true} />
                          available
                        </label>
                        <button
                          type="button"
                          className={btnGhost}
                          onClick={async () => {
                            const raw = (document.getElementById(`price-${t.key}`) as HTMLInputElement).value;
                            const avail = (document.getElementById(`avail-${t.key}`) as HTMLInputElement).checked;
                            await setPrice({
                              data: {
                                beat_id: selected.id,
                                tier_key: t.key,
                                price_cents: raw ? Math.round(Number(raw) * 100) : null,
                                available: avail,
                              },
                            });
                            setMessage(`${t.name} pricing saved.`);
                            void refresh();
                          }}
                        >
                          Save
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </form>
        )}
      </section>

      {/* ---------- Tiers ---------- */}
      <section className={`${card} mt-8`} aria-labelledby="tiers-admin">
        <h2 id="tiers-admin" className="font-serif text-2xl text-gold-luminous">
          Licence tiers
        </h2>
        <div className="mt-5 space-y-4">
          {(d?.tiers ?? []).map((t: any) => (
            <details key={t.key} className="rounded-xl border hairline p-4">
              <summary className="cursor-pointer text-ivory">
                {t.name} <span className="text-xs text-muted-foreground">({t.key}) · {formatMoney(t.price_cents, t.currency)}</span>
              </summary>
              <form
                className="mt-4 grid gap-3 sm:grid-cols-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  const result = await saveTier({
                    data: {
                      key: t.key,
                      name: String(f.get("name") ?? ""),
                      tagline: String(f.get("tagline") ?? ""),
                      price_cents: Math.round(Number(f.get("price") ?? 0) * 100),
                      currency: t.currency ?? "usd",
                      file_formats: String(f.get("formats") ?? "").split(",").map((s) => s.trim()).filter(Boolean),
                      stream_limit: String(f.get("stream_limit") ?? ""),
                      video_limit: String(f.get("video_limit") ?? ""),
                      distribution_limit: String(f.get("distribution_limit") ?? ""),
                      monetization: String(f.get("monetization") ?? ""),
                      performance_rights: String(f.get("performance_rights") ?? ""),
                      territory: String(f.get("territory") ?? "Worldwide"),
                      term: String(f.get("term") ?? "Perpetual"),
                      credit_required: String(f.get("credit") ?? ""),
                      restrictions: String(f.get("restrictions") ?? "").split("\n").map((s) => s.trim()).filter(Boolean),
                      is_quote_only: f.get("quote_only") === "on",
                      active: f.get("active") === "on",
                      sort_order: Number(f.get("sort_order") ?? 0),
                    },
                  });
                  setMessage("error" in result ? result.error : `${t.name} saved.`);
                  void refresh();
                }}
              >
                {[
                  ["name", "Name", t.name],
                  ["tagline", "Tagline", t.tagline ?? ""],
                  ["formats", "File formats (comma separated)", (t.file_formats ?? []).join(", ")],
                  ["stream_limit", "Stream limit", t.stream_limit ?? ""],
                  ["video_limit", "Video limit", t.video_limit ?? ""],
                  ["distribution_limit", "Distribution limit", t.distribution_limit ?? ""],
                  ["monetization", "Monetization", t.monetization ?? ""],
                  ["performance_rights", "Performance rights", t.performance_rights ?? ""],
                  ["territory", "Territory", t.territory ?? ""],
                  ["term", "Term", t.term ?? ""],
                  ["credit", "Credit requirement", t.credit_required ?? ""],
                ].map(([name, lbl, val]) => (
                  <div key={name as string}>
                    <label className={label} htmlFor={`${t.key}-${name}`}>{lbl}</label>
                    <input id={`${t.key}-${name}`} name={name as string} defaultValue={val as string} className={input} />
                  </div>
                ))}
                <div>
                  <label className={label} htmlFor={`${t.key}-price`}>Price (USD)</label>
                  <input id={`${t.key}-price`} name="price" type="number" step="0.01" defaultValue={(t.price_cents / 100).toFixed(2)} className={input} />
                </div>
                <div>
                  <label className={label} htmlFor={`${t.key}-sort`}>Sort order</label>
                  <input id={`${t.key}-sort`} name="sort_order" type="number" defaultValue={t.sort_order} className={input} />
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor={`${t.key}-restrictions`}>Restrictions (one per line)</label>
                  <textarea id={`${t.key}-restrictions`} name="restrictions" rows={4} defaultValue={(t.restrictions ?? []).join("\n")} className={input} />
                </div>
                <label className="flex items-center gap-2 text-sm text-ivory">
                  <input type="checkbox" name="quote_only" defaultChecked={t.is_quote_only} /> Quote only
                </label>
                <label className="flex items-center gap-2 text-sm text-ivory">
                  <input type="checkbox" name="active" defaultChecked={t.active} /> Active
                </label>
                <button type="submit" className={`${btn} sm:col-span-2 justify-self-start`}>Save tier</button>
              </form>
            </details>
          ))}
        </div>
      </section>

      {/* ---------- Templates ---------- */}
      <section className={`${card} mt-8`} aria-labelledby="templates-admin">
        <h2 id="templates-admin" className="font-serif text-2xl text-gold-luminous">
          Agreement templates
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Saving publishes a new version. Issued licences keep their own immutable snapshot and are never affected.
        </p>
        <ul className="mt-4 space-y-1 text-sm text-muted-foreground">
          {(d?.templates ?? []).map((t: any) => (
            <li key={t.id}>
              {t.tier_key} · v{t.version} {t.active ? "(active)" : ""} — {t.title}
            </li>
          ))}
        </ul>
        <form
          className="mt-5 grid gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const result = await saveTemplate({
              data: {
                tier_key: String(f.get("tier_key") ?? ""),
                title: String(f.get("title") ?? ""),
                body_md: String(f.get("body_md") ?? ""),
              },
            });
            setMessage("error" in result ? result.error : `New template version v${result.version} published.`);
            void refresh();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="tpl_tier">Tier key</label>
              <input id="tpl_tier" name="tier_key" required className={input} placeholder="basic" />
            </div>
            <div>
              <label className={label} htmlFor="tpl_title">Title</label>
              <input id="tpl_title" name="title" required className={input} />
            </div>
          </div>
          <div>
            <label className={label} htmlFor="tpl_body">Agreement text</label>
            <textarea id="tpl_body" name="body_md" rows={8} required className={input} />
          </div>
          <button type="submit" className={`${btn} justify-self-start`}>Publish new version</button>
        </form>
      </section>

      {/* ---------- Requests ---------- */}
      <section className={`${card} mt-8`} aria-labelledby="requests-admin">
        <h2 id="requests-admin" className="font-serif text-2xl text-gold-luminous">
          Custom requests ({d?.requests.length ?? 0})
        </h2>
        {(d?.requests.length ?? 0) === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No requests yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {(d?.requests ?? []).map((r: any) => (
              <li key={r.id} className="rounded-xl border hairline p-4 text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="text-ivory">
                    {r.contact_name} · {r.contact_email}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-gold">{r.status}</span>
                </div>
                <p className="mt-2 text-muted-foreground">{r.intended_use}</p>

                {/* Email delivery state for this request */}
                <ul className="mt-3 space-y-1 text-xs">
                  {(d?.notifications ?? [])
                    .filter((n: any) => n.request_id === r.id)
                    .map((n: any) => (
                      <li key={n.id} className="flex flex-wrap items-center gap-2">
                        <span className="uppercase tracking-[0.18em] text-gold/80">{n.channel}</span>
                        <span
                          className={
                            n.status === "sent"
                              ? "text-emerald-400"
                              : n.status === "failed"
                                ? "text-red-400"
                                : "text-muted-foreground"
                          }
                        >
                          {n.status}
                        </span>
                        <span className="text-muted-foreground">{n.recipient}</span>
                        {n.attempts ? (
                          <span className="text-muted-foreground">· {n.attempts} attempt(s)</span>
                        ) : null}
                        {n.error ? <span className="text-red-400/80">· {n.error}</span> : null}
                        {n.status !== "sent" && (
                          <button
                            type="button"
                            className="rounded-full border hairline px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-gold hover:border-gold"
                            onClick={async () => {
                              const res: any = await retryNotification({ data: { id: n.id } });
                              setMessage(
                                res?.error
                                  ? `Retry failed: ${res.error}`
                                  : `Retry ${res?.result?.status ?? "done"} (${n.channel}).`,
                              );
                              void refresh();
                            }}
                          >
                            Resend
                          </button>
                        )}
                      </li>
                    ))}
                  {(d?.notifications ?? []).filter((n: any) => n.request_id === r.id).length === 0 && (
                    <li className="text-muted-foreground">No email records for this request.</li>
                  )}
                </ul>

                <div className="mt-3 flex flex-wrap gap-2">
                  {(["reviewing", "quoted", "accepted", "declined", "closed"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={btnGhost}
                      onClick={async () => {
                        await updateRequest({ data: { id: r.id, status: s } });
                        void refresh();
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---------- Licences + audit ---------- */}
      <section className={`${card} mt-8`} aria-labelledby="licences-admin">
        <h2 id="licences-admin" className="font-serif text-2xl text-gold-luminous">
          Issued licences ({d?.licenses.length ?? 0})
        </h2>
        {(d?.licenses.length ?? 0) === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No licences issued yet.</p>
        ) : (
          <ul className="mt-4 space-y-1 text-sm text-muted-foreground">
            {(d?.licenses ?? []).map((l: any) => (
              <li key={l.id}>
                {l.license_number} · {l.beat_slug} · {l.tier_name} · {formatMoney(l.price_cents, l.currency)} ·{" "}
                {l.environment} · {l.status}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`${card} mt-8`} aria-labelledby="checklist">
        <h2 id="checklist" className="font-serif text-2xl text-gold-luminous">
          Launch-readiness checklist
        </h2>
        <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
          {CHECKLIST.map((c) => (
            <li key={c} className="pl-4 -indent-4">
              — {c}
            </li>
          ))}
        </ul>
      </section>

      <section className={`${card} mt-8`} aria-labelledby="audit">
        <h2 id="audit" className="font-serif text-2xl text-gold-luminous">
          Audit log
        </h2>
        <ul className="mt-4 space-y-1 text-xs text-muted-foreground">
          {(d?.audit ?? []).map((a: any) => (
            <li key={a.id}>
              {new Date(a.created_at).toISOString().slice(0, 19).replace("T", " ")} · {a.action} · {a.entity} ·{" "}
              {a.entity_id ?? "—"}
            </li>
          ))}
          {(d?.audit.length ?? 0) === 0 && <li>No administrative actions recorded yet.</li>}
        </ul>
      </section>
    </div>
  );
}
